'use client';

import { useState, useMemo } from 'react';
import { IEvent } from '@/lib/types';
import { SupportedLanguages } from '@/types/globals';
import { formatDate, formatTime } from '@/lib/dateUtils';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isBetween from 'dayjs/plugin/isBetween';
import CalendarIcon from '@/assets/icon/calendar.svg';
import ArrowRightIcon from '@/assets/icon/arrow-right.svg';
import { useEventCalendar, useAllEvents } from '@/hooks/useEventCalendar';
import Link from 'next/link';
import { useTrackNavigation } from '@/hooks/useNavigationStore';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

type CalendarView = 'month' | 'week';

interface EventCalendarProps {
  events?: IEvent[]; // Optional - will use API if not provided
  locale: SupportedLanguages;
  basePath: string;
  className?: string;
}

interface CalendarEvent extends IEvent {
  startDate: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
}

const text = {
  en: {
    today: 'Today',
    month: 'Month',
    week: 'Week',
    noEvents: 'No events scheduled',
    viewEvent: 'View Event',
    seeDetails: 'See Details',
    eventWebsite: 'Event Website',
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    weekdaysFull: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    loadingCalendar: 'Loading calendar...',
    failedToLoadCalendar: 'Failed to load calendar events',
    showAll: 'Show All',
    more: 'More',
    learnMore: 'Learn More ',
  },
  fr: {
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    noEvents: 'Aucun événement programmé',
    viewEvent: "Voir l'événement",
    seeDetails: 'Voir les détails',
    eventWebsite: "Site de l'événement",
    months: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    weekdays: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    weekdaysFull: [
      'Dimanche',
      'Lundi',
      'Mardi',
      'Mercredi',
      'Jeudi',
      'Vendredi',
      'Samedi',
    ],
    loadingCalendar: 'Chargement du calendrier...',
    failedToLoadCalendar: 'Échec du chargement des événements du calendrier',
    showAll: 'Tout afficher',
    more: 'Plus',
    learnMore: 'En savoir plus ',
  },
};

export function EventCalendar({
  events,
  locale,
  basePath,
  className,
}: EventCalendarProps) {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [view] = useState<CalendarView>('month');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const translatedText = text[locale];
  useTrackNavigation();

  // Use API hook if events are not provided
  const calendarQuery = useEventCalendar({
    locale,
    view,
    currentDate,
    enabled: !events, // Only use API if events prop is not provided
  });

  // Fallback to all events if calendar API fails
  const allEventsQuery = useAllEvents(locale, !events && calendarQuery.isError);

  // Determine which events to use
  const sourceEvents = useMemo(() => {
    if (events) return events;
    if (calendarQuery.data?.events) return calendarQuery.data.events;
    if (allEventsQuery.data) return allEventsQuery.data;
    return [];
  }, [events, calendarQuery.data, allEventsQuery.data]);

  // Convert events to calendar events with parsed dates
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    let filteredEvents = sourceEvents.filter(
      event => event.acf?.event_date_start && event.acf?.display,
    );

    // Filter by location if selected
    if (selectedLocation && selectedLocation !== 'all') {
      filteredEvents = filteredEvents.filter(event => {
        const eventLocation =
          event.acf?.event_location?.name?.toLowerCase() || '';
        return eventLocation.includes(selectedLocation.toLowerCase());
      });
    }

    return filteredEvents
      .map(event => ({
        ...event,
        startDate: dayjs(event.acf.event_date_start),
        endDate: event.acf.event_date_end
          ? dayjs(event.acf.event_date_end)
          : undefined,
      }))
      .sort(
        (eventA, eventB) =>
          eventA.startDate.valueOf() - eventB.startDate.valueOf(),
      );
  }, [sourceEvents, selectedLocation]);

  // Get unique locations for filter pills
  const availableLocations = useMemo(() => {
    const locations = sourceEvents
      .map(event => event.acf?.event_location?.name)
      .filter((location): location is string => !!location)
      .reduce((acc, location) => {
        acc.add(location);
        return acc;
      }, new Set<string>());

    return Array.from(locations).sort();
  }, [sourceEvents]);

  // Loading and error states
  const isLoading =
    !events && (calendarQuery.isLoading || allEventsQuery.isLoading);
  const error = !events && calendarQuery.error && allEventsQuery.error;

  // Format functions
  const formatDateLocal = (dateString: string) => {
    const formatted = formatDate(dateString, locale);
    return {
      year: formatted.year.toString(),
      day: formatted.day.toString(),
      month: formatted.month,
    };
  };

  const formatTimeLocal = (timeString: string) => {
    if (!timeString) return '';
    return formatTime(timeString, locale);
  };

  // Navigation functions
  const goToPrevious = () => {
    if (view === 'month') {
      setCurrentDate(currentDate.subtract(1, 'month'));
    } else if (view === 'week') {
      setCurrentDate(currentDate.subtract(1, 'week'));
    }
  };
  const goToNext = () => {
    if (view === 'month') {
      setCurrentDate(currentDate.add(1, 'month'));
    } else if (view === 'week') {
      setCurrentDate(currentDate.add(1, 'week'));
    }
  };

  // Get events for a specific date
  const getEventsForDate = (date: dayjs.Dayjs) => {
    return calendarEvents.filter(event => {
      const eventStart = event.startDate.startOf('day');
      const eventEnd = event.endDate
        ? event.endDate.endOf('day')
        : eventStart.endOf('day');
      const checkDate = date.startOf('day');

      return (
        checkDate.isSame(eventStart) ||
        checkDate.isSame(eventEnd) ||
        (checkDate.isAfter(eventStart) && checkDate.isBefore(eventEnd))
      );
    });
  };

  // Helper to determine if event spans multiple days
  const getEventSpan = (event: CalendarEvent, dayDate: dayjs.Dayjs) => {
    const eventStart = event.startDate.startOf('day');
    const eventEnd = event.endDate ? event.endDate.startOf('day') : eventStart;
    const currentDay = dayDate.startOf('day');

    const isStart = currentDay.isSame(eventStart);
    const isEnd = currentDay.isSame(eventEnd);
    const isMiddle =
      currentDay.isAfter(eventStart) && currentDay.isBefore(eventEnd);

    return {
      isStart,
      isEnd,
      isMiddle,
      isMultiDay: !eventStart.isSame(eventEnd),
    };
  };

  // Render month view
  const renderMonthView = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');

    const weeks = [];
    let currentWeek = startDate;

    while (
      currentWeek.isBefore(endDate, 'day') ||
      currentWeek.isSame(endDate, 'day')
    ) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = currentWeek.add(i, 'day');
        const isCurrentMonth = day.isSame(currentDate, 'month');
        const isToday = day.isSame(dayjs(), 'day');
        const dayEvents = getEventsForDate(day);

        days.push(
          <div
            key={day.format('YYYY-MM-DD')}
            className={`relative min-h-[140px] border-r border-b border-gray-200 p-3 ${
              !isCurrentMonth
                ? 'text-qf-purple/60 [&:has(a:hover)_.date-circle]:bg-qf-purple bg-gray-50 [&:has(a:hover)]:bg-[#533180] [&:has(a:hover)]:text-white [&:has(a:hover)_.date-circle]:border-1 [&:has(a:hover)_.date-circle]:border-white [&:has(a:hover)_.date-circle]:text-white'
                : 'text-qf-purple [&:has(a:hover)_.date-circle]:bg-qf-purple bg-white [&:has(a:hover)]:bg-[#533180] [&:has(a:hover)]:text-white [&:has(a:hover)_.date-circle]:border-1 [&:has(a:hover)_.date-circle]:border-white [&:has(a:hover)_.date-circle]:text-white'
            } ${isToday ? 'bg-qf-purple' : ''}`}
          >
            {/* Date number */}
            <div className="mb-2 flex justify-end">
              <div
                className={`date-circle flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  isToday
                    ? 'bg-qf-purple text-white'
                    : isCurrentMonth
                      ? 'bg-[#EAEAEA80] text-gray-900'
                      : 'bg-[#EAEAEA80] text-gray-400'
                }`}
              >
                {day.date()}
              </div>
            </div>

            {/* Events */}
            <div className="space-y-1">
              {dayEvents.map((event, index) => {
                const span = getEventSpan(event, day);

                return (
                  <div
                    key={`${event.id}-${day.format('YYYY-MM-DD')}`}
                    className={`font-inter relative ${index < 2 ? 'block hover:!z-10 [&:has(a:hover)~&]:opacity-0' : 'hidden'}`}
                  >
                    {span.isStart || !span.isMultiDay ? (
                      <Link
                        href={`/${basePath}/${event.slug}`}
                        className={`font-source-sans-pro hover:bg-qf-purple group group/event block w-full rounded p-2 text-left text-xs transition-all hover:text-white ${
                          span.isMultiDay ? 'rounded-r-none border-r-0' : ''
                        }`}
                      >
                        <div className="text-graphite-900 pb-1 text-xs opacity-75 group-hover:text-white">
                          {formatDateLocal(event.acf.event_date_start).month}{' '}
                          {formatDateLocal(event.acf.event_date_start).day}
                          {event.acf.event_date_end &&
                            span.isMultiDay &&
                            ` - ${formatDateLocal(event.acf.event_date_end).month} ${formatDateLocal(event.acf.event_date_end).day}`}
                        </div>
                        <div className="font-source-sans-pro wrap pb-1 text-lg font-semibold">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: event.title.rendered.replace(
                                /<[^>]*>/g,
                                '',
                              ),
                            }}
                          />
                        </div>
                        {event.acf?.event_location && (
                          <div className="font-inter text-graphite-900 truncate text-xs group-hover:text-white">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: event.acf.event_location.name,
                              }}
                            />
                          </div>
                        )}
                        <div className="text-qf-purple mt-2 hidden w-fit rounded-xl bg-white px-2 text-sm font-medium group-hover:flex group-hover:items-center group-hover:gap-1">
                          {translatedText.learnMore}
                          <ArrowRightIcon className="text-qf-purple h-4 w-4" />
                        </div>
                      </Link>
                    ) : span.isMiddle ? (
                      <Link
                        href={`/${basePath}/${event.slug}`}
                        className={`font-source-sans-pro hover:bg-qf-purple group group/event block w-full border-t border-b bg-white p-2 text-left text-xs transition-all hover:text-white ${
                          span.isMultiDay ? 'rounded-r-none border-r-0' : ''
                        } border-r-0 border-l-0`}
                      >
                        {span.isMultiDay && (
                          <div className="text-graphite-900 pb-1 text-xs group-hover:text-white">
                            {formatDateLocal(event.acf.event_date_start).month}{' '}
                            {formatDateLocal(event.acf.event_date_start).day}
                            {event.acf.event_date_end &&
                              ` - ${formatDateLocal(event.acf.event_date_end).month} ${formatDateLocal(event.acf.event_date_end).day}`}
                          </div>
                        )}
                        <div className="font-source-sans-pro wrap pb-1 text-lg font-semibold">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: event.title.rendered.replace(
                                /<[^>]*>/g,
                                '',
                              ),
                            }}
                          />
                        </div>
                        {event.acf?.event_location && (
                          <div className="font-inter text-graphite-900 truncate text-xs group-hover:text-white">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: event.acf.event_location.name,
                              }}
                            />
                          </div>
                        )}
                        <div className="text-qf-purple mt-2 hidden w-fit rounded-xl bg-white px-2 text-sm font-medium group-hover:flex group-hover:items-center group-hover:gap-1">
                          {translatedText.learnMore}
                          <ArrowRightIcon className="text-qf-purple h-4 w-4" />
                        </div>
                      </Link>
                    ) : span.isEnd ? (
                      <Link
                        href={`/${basePath}/${event.slug}`}
                        className={`font-source-sans-pro text-qf-purple hover:bg-qf-purple group group/event block w-full rounded-r-md p-2 text-left text-xs transition-all hover:text-white ${
                          span.isMultiDay ? 'rounded-l-none' : ''
                        } border-l-0`}
                      >
                        {span.isMultiDay && (
                          <div className="text-graphite-900 pb-1 text-xs group-hover:text-white">
                            {formatDateLocal(event.acf.event_date_start).month}{' '}
                            {formatDateLocal(event.acf.event_date_start).day}
                            {event.acf.event_date_end &&
                              ` - ${formatDateLocal(event.acf.event_date_end).month} ${formatDateLocal(event.acf.event_date_end).day}`}
                          </div>
                        )}
                        <div
                          className="font-source-sans-pro wrap pb-1 text-lg font-semibold"
                          dangerouslySetInnerHTML={{
                            __html: event.title.rendered,
                          }}
                        />
                        {event.acf?.event_location && (
                          <div
                            className="font-inter text-graphite-900 truncate text-xs group-hover:text-white"
                            dangerouslySetInnerHTML={{
                              __html: event.acf.event_location.name,
                            }}
                          />
                        )}
                        <div className="text-qf-purple mt-2 hidden w-fit rounded-xl bg-white px-2 text-sm font-medium group-hover:flex group-hover:items-center group-hover:gap-1">
                          {translatedText.learnMore}
                          <ArrowRightIcon className="text-qf-purple h-4 w-4" />
                        </div>
                      </Link>
                    ) : null}
                  </div>
                );
              })}

              {/* Show more indicator */}
              {dayEvents.length > 2 && (
                <button className="py-1 text-xs text-gray-500 group-hover/cell:opacity-0 hover:text-gray-700">
                  +{dayEvents.length - 2} {translatedText.more}
                </button>
              )}
            </div>
          </div>,
        );
      }
      weeks.push(...days);
      currentWeek = currentWeek.add(1, 'week');
    }

    return (
      <div className="overflow-hidden rounded-b-lg border-x border-b border-gray-200 bg-white">
        {/* Header */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {translatedText.weekdays.map((day, index) => (
            <div
              key={index}
              className="border-r border-gray-200 p-2 text-center text-sm font-semibold text-gray-500 last:border-r-0 md:p-4"
            >
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7">{weeks}</div>
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const startOfWeek = currentDate.startOf('week');
    const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));

    return (
      <div className="overflow-hidden rounded-lg bg-white">
        {/* Header */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {days.map((day, index) => {
            const isToday = day.isSame(dayjs(), 'day');
            return (
              <div
                key={index}
                className={`p-4 text-center ${isToday ? 'bg-qf-purple' : ''}`}
              >
                <div className="text-center text-sm text-gray-500">
                  {translatedText.weekdays[index]}
                </div>
                <div className="flex justify-end">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold ${
                      isToday
                        ? 'bg-qf-purple text-white'
                        : 'bg-[#EAEAEA80] text-gray-900'
                    }`}
                  >
                    {day.date()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* Events */}
        <div className="grid min-h-[400px] grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            return (
              <div
                key={index}
                className="border-r border-gray-200 p-2 last:border-r-0"
              >
                <div className="space-y-2">
                  {dayEvents.map(event => {
                    return (
                      <Link
                        key={event.id}
                        href={`/${basePath}/${event.slug}`}
                        className={`font-source-sans-pro hover:bg-qf-purple group group/event block w-full rounded bg-white p-2 text-left transition-all hover:text-white`}
                      >
                        <div className="font-source-sans-pro wrap mb-1 text-lg text-sm font-medium font-semibold">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: event.title.rendered.replace(
                                /<[^>]*>/g,
                                '',
                              ),
                            }}
                          />
                        </div>
                        {event.acf?.start_time && (
                          <div className="font-source-sans-pro text-graphite-900 text-xs group-hover:text-white">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: formatTimeLocal(event.acf.start_time),
                              }}
                            />
                          </div>
                        )}
                        {event.acf?.event_location && (
                          <div className="font-inter text-graphite-900 truncate text-xs group-hover:text-white">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: event.acf.event_location.name,
                              }}
                            />
                          </div>
                        )}
                        <div className="text-qf-purple mt-2 hidden w-fit rounded-xl bg-white px-2 text-sm font-medium group-hover:flex group-hover:items-center group-hover:gap-1">
                          {translatedText.learnMore}
                          <ArrowRightIcon className="text-qf-purple h-4 w-4" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`mx-auto max-w-7xl ${className || ''}`}>
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <div className="border-qf-purple mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
          <p className="text-gray-500">{text[locale].loadingCalendar}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`mx-auto max-w-7xl ${className || ''}`}>
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <CalendarIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-gray-500">{text[locale].failedToLoadCalendar}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`mx-auto max-w-7xl px-4 ${className || ''}`}>
      {/* Location Filters */}
      <div className="-mx-4 mb-6 px-4 md:mx-0 md:px-0">
        <div className="bg-warmgrey-900 inline-flex flex-wrap gap-2 rounded-2xl p-2 md:rounded-full">
          <button
            onClick={() => setSelectedLocation(null)}
            className={`h-9 rounded-full px-4 py-1.5 text-sm font-medium transition-colors md:h-12 md:px-5 ${
              !selectedLocation
                ? 'bg-qf-purple text-white'
                : 'hover:border-qf-purple border border-gray-300 bg-white text-gray-900'
            }`}
          >
            <span
              dangerouslySetInnerHTML={{ __html: translatedText.showAll }}
            />
          </button>
          {availableLocations.slice(0, 5).map(location => (
            <button
              key={location}
              onClick={() =>
                setSelectedLocation(
                  location === selectedLocation ? null : location,
                )
              }
              className={`h-9 rounded-full px-4 py-1.5 text-sm font-medium transition-colors md:h-12 md:px-5 ${
                selectedLocation === location
                  ? 'bg-qf-purple text-white'
                  : 'text-qf-black hover:border-qf-purple border border-gray-300 bg-white'
              }`}
            >
              {location}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Header */}
      <div className="rounded-t-lg bg-white p-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={goToPrevious}
            className="hover:bg-qf-purple flex h-8 w-8 items-center justify-center rounded-full bg-[#EAEAEA80] transition-colors hover:text-white"
            aria-label="Previous"
          >
            <ArrowRightIcon className="h-5 w-5 rotate-180 transform text-gray-600 hover:text-white" />
          </button>

          <h2 className="font-source-serif-pro text-qf-purple text-2xl font-semibold">
            {view === 'month' &&
              `${translatedText.months[currentDate.month()]} ${currentDate.year()}`}
            {view === 'week' &&
              `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format('MMM D, YYYY')}`}
          </h2>

          <button
            onClick={goToNext}
            className="hover:bg-qf-purple flex h-8 w-8 items-center justify-center rounded-full bg-[#EAEAEA80] transition-colors hover:text-white"
            aria-label="Next"
          >
            <ArrowRightIcon className="h-5 w-5 text-gray-600 hover:text-white" />
          </button>
        </div>
      </div>

      {/* Calendar content */}
      <div>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
      </div>
    </div>
  );
}
